import { browser, dev } from '$app/environment';
import { page } from '$app/state';
import { PUBLIC_SHIPPING_TOKEN } from '$env/static/public';
import { isActiveProduct } from '$lib/api/stripe';
import { trackInitiateCheckout } from '$lib/segment/client';
import log from '$lib/utils/log';
import { getStripeCheckoutSession } from '$lib/utils/stripe.utils';
import { event } from '$lib/utils/tracking';
import type Stripe from 'stripe';
import { CheckoutSessionStorage, type CheckoutSessionData } from './checkoutSessionStorage';
import { invalidateAllPrices, invalidatePrice, ReactiveProduct } from './reactiveProduct.svelte';
import type { StripeProductData } from '$lib/storyblok/types';
import { getPrice, getShippingRate } from '$lib/utils/stripe';
import { shoppingCart } from './shoppingCart.svelte';
import type { ProductDetails } from '$lib/storyblok/blocks/cart/productDetails.svelte';
import type { CartProductAddon } from '$lib/storyblok/blocks/cart/cartProductAddon.svelte';

// @internal
export class CheckoutSession {
	#updatingBundle = $state(true);
	#processing = $state(false);
	#lastError = $state<string | null>(null);

	#cartData = $state<CheckoutSessionData>({});
	#shippingData = $state<Stripe.ShippingRate | null>(null);

	#syncedCartData = $state<CheckoutSessionData | null>(null);
	#stripeSession = $state<Promise<Stripe.Response<Stripe.Checkout.Session> | Error | null>>(
		Promise.resolve(null)
	);
	#currentAbortController: AbortController | null = null;

	#shippingRateCache = new Map<string, Promise<Stripe.ShippingRate>>();

	get updatingBundle() {
		return this.#updatingBundle;
	}

	get processing() {
		return this.#processing;
	}

	get lastError() {
		return this.#lastError;
	}

	get empty() {
		return !this.#cartData.product;
	}

	get data() {
		return this.#cartData;
	}

	get selectedProductImage() {
		let image = {
			alt: '',
			title: '',
			filename: 'https://a-us.storyblok.com/f/1019164/448x448/8ae5336110/mill-food-recycler.png'
		};

		if (
			this.selectedProduct &&
			['product_details', 'cart_product_addon'].includes(this.selectedProduct.component)
		) {
			if (this.selectedProduct.product_image?.filename) {
				image = this.selectedProduct.product_image;
			} else if (this.baseProduct?.product_image?.filename) {
				image = this.baseProduct?.product_image;
			}
		}

		return image;
	}

	baseProduct = $derived(
		shoppingCart.products.find(
			(product) =>
				product.component === 'product_details' &&
				product.stripe_product?.priceLookupKey ===
				checkoutSession.selectedBaseStripeProduct?.priceLookupKey
		)
	);

	selectedProduct = $derived(
		this.#cartData?.product
			? shoppingCart.products.find((p) => {
				if (p.component === 'product_details')
					return (
						p.stripe_product &&
						p.stripe_product.priceLookupKey === this.#cartData.product?.price.lookup_key
					);

				return (
					p.addon_product &&
					p.addon_product.priceLookupKey === this.#cartData.product?.price.lookup_key
				);
			})
			: undefined
	);

	get selectedLookupKey() {
		const selected = this.selectedProduct;

		if (!selected) return;

		if (selected.component === 'product_details') {
			return selected.stripe_product?.priceLookupKey;
		}

		if (selected.component === 'cart_product_addon') {
			return selected.addon_product?.priceLookupKey;
		}
	}

	get selectedBaseStripeProduct() {
		const selected = this.selectedProduct;

		if (!selected) return;

		if (selected.component !== 'cart_product_addon') return;

		return selected.base_product;
	}

	get canUseFinancing() {
		const selected = this.selectedProduct;

		if (selected?.component === 'product_details') return selected.financing_options !== 'card_only';

		return this.baseProduct?.financing_options !== 'card_only';
	}

	equals(other: CheckoutSessionData) {
		if (this.#cartData.accessCode !== other.accessCode) return false;
		if (this.#cartData.afterSubmitText !== other.afterSubmitText) return false;
		if (this.#cartData.isTrial !== other.isTrial) return false;
		if (this.#cartData.productBundle !== other.productBundle) return false;
		if (this.#cartData.termsOfService !== other.termsOfService) return false;
		if (this.#cartData.financingOptions !== other.financingOptions) return false;
		if (this.#cartData.shippingId !== other.shippingId) return false;
		if (this.#cartData.successPage !== other.successPage) return false;
		if (this.#cartData.etas?.max !== other.etas?.max) return false;
		if (this.#cartData.etas?.min !== other.etas?.min) return false;
		if (this.#cartData.adjustableQuantity?.enabled !== other.adjustableQuantity?.enabled)
			return false;
		if (this.#cartData.adjustableQuantity?.max !== other.adjustableQuantity?.max) return false;

		const currentLabels = this.#cartData.userLabels;
		const otherLabels = other.userLabels;

		if (currentLabels?.size !== otherLabels?.size) return false;
		if (currentLabels) {
			if (!Array.from(otherLabels || []).every((label) => currentLabels.has(label))) return false;
		}

		if ((!this.#cartData.product && other.product) || (this.#cartData.product && !other.product))
			return false;

		if (!this.#cartData.product && !other.product) return true;

		return this.#cartData.product && other.product && this.#cartData.product.equals(other.product);
	}

	get customOrderSuccessPage() {
		if (this.baseProduct?.component === 'product_details' && this.baseProduct.custom_order_success_page) {
			return this.baseProduct.custom_order_success_page.full_slug;
		}

		if (this.selectedProduct?.component === 'product_details' && this.selectedProduct.custom_order_success_page) {
			return this.selectedProduct.custom_order_success_page?.full_slug;
		}

		return this.#cartData.successPage;
	}

	async #createStripeSession(): Promise<Stripe.Response<Stripe.Checkout.Session> | Error | null> {
		try {
			if (this.#currentAbortController) {
				this.#currentAbortController.abort('stale_checkout_data');
			}

			this.#currentAbortController = new AbortController();

			const session = await getStripeCheckoutSession(
				{ ...this.#cartData, successPage: this.customOrderSuccessPage },
				this.#currentAbortController.signal
			);
			this.#syncedCartData = this.#cartData;

			if (dev) {
				log.scope('Checkout').debug('Checkout session was successfully generated.', { session });
			}

			return session;
		} catch (error) {
			if (error === 'stale_checkout_data') {
				if (dev) {
					log.scope('Stripe').debug('Checkout request aborted (stale data)');
				}
				return null;
			}

			if (error instanceof Error) {
				return error;
			}

			log.scope('Stripe').error('Cannot create checkout session', { error });
			return null;
		} finally {
			this.#updatingBundle = false;
		}
	}

	async #setStripeSession() {
		if (
			this.empty ||
			!this.#cartData.product?.price?.lookup_key ||
			!isActiveProduct(this.#cartData.product?.price?.product)
		) {
			this.#currentAbortController = null;
			this.#syncedCartData = this.#cartData;
			this.#stripeSession = Promise.resolve(null);
			this.#updatingBundle = false;
			return;
		}

		if (this.#syncedCartData && this.equals(this.#syncedCartData)) {
			this.#currentAbortController = null;
			this.#updatingBundle = false;

			if (dev) {
				log.scope('Checkout').debug('Checkout session is already in sync');
			}
			return;
		}

		this.#stripeSession = this.#createStripeSession();
	}

	#handleProductUpdate = () => {
		this.#setStripeSession();
		CheckoutSessionStorage.store(this.#cartData);
	};

	async #setShippingData(shippingId: string | undefined) {
		if (!shippingId?.length) {
			this.#shippingData = null;
			return;
		}

		try {
			let ratePromise = this.#shippingRateCache.get(shippingId);
			if (!ratePromise) {
				ratePromise = getShippingRate(shippingId);
				this.#shippingRateCache.set(shippingId, ratePromise);
			}
			this.#shippingData = await ratePromise;
		} catch (e) {
			log.scope('Checkout').error('Cannot fetch initial shipping rate', {
				shippingId,
				error: (e as Error).message
			});
			this.#shippingData = null;
		}
	}

	constructor() {
		if (!browser) return;

		this.#cartData = CheckoutSessionStorage.fetch();

		this.#setStripeSession();
		this.#setShippingData(this.#cartData.shippingId);

		this.#cartData.product?.onChange(this.#handleProductUpdate);

		$effect.root(() => {
			$effect(() => {
				const {
					url: { searchParams }
				} = page;

				if (searchParams.get('sh-token') === PUBLIC_SHIPPING_TOKEN) {
					this.#cartData.shippingId = undefined;
					CheckoutSessionStorage.store(this.#cartData);
				}
			});
		});
	}

	get isFreeShipping() {
		return !this.#cartData.shippingId;
	}

	get shippingCosts() {
		return this.#shippingData?.fixed_amount?.amount || 0;
	}

	async #setCartData(data: CheckoutSessionData) {
		this.#updatingBundle =
			!this.#cartData.productBundle || data.productBundle !== this.#cartData.productBundle;

		const previousShippingId = this.#cartData?.shippingId;

		let selectedLookupKey = data.product?.price.lookup_key;

		const selectedBaseProduct = shoppingCart.products.find((p) => {
			if (p.component === 'cart_product_addon')
				return p.addon_product && p.addon_product.priceLookupKey === selectedLookupKey;
		}) as unknown as CartProductAddon | undefined;

		if (selectedBaseProduct) {
			selectedLookupKey = selectedBaseProduct.base_product?.priceLookupKey;
		}

		const selectedProduct = shoppingCart.products.find((p) => {
			if (p.component === 'product_details')
				return p.stripe_product && p.stripe_product.priceLookupKey === selectedLookupKey;
		}) as unknown as ProductDetails | undefined;

		const etas =
			selectedProduct?.max_weeks_shipping && selectedProduct.min_weeks_shipping?.length
				? {
					min: parseInt(selectedProduct.min_weeks_shipping, 10),
					max: parseInt(selectedProduct.max_weeks_shipping, 10)
				}
				: undefined;

		const newData: CheckoutSessionData = {
			...data,
			etas,
			productSubline: selectedProduct?.description,
			financingOptions: selectedProduct?.financing_options
		};

		if (this.equals(newData)) {
			this.#updatingBundle = false;
			return;
		}

		this.#cartData = newData;

		if (browser) {
			this.#setStripeSession();
			CheckoutSessionStorage.store(this.#cartData);
			this.#cartData.product?.onChange(this.#handleProductUpdate);
		} else {
			this.#updatingBundle = false;
		}

		if (this.#cartData.shippingId === previousShippingId) return;

		this.#setShippingData(this.#cartData.shippingId);
	}

	async configure(config: Omit<CheckoutSessionData, 'product'> = {}) {
		const newCartData = {
			...this.#cartData,
			...config
		};

		if (this.equals(newCartData)) {
			if (dev) {
				log.scope('Checkout').debug(`Cart configuration didn't change`);
			}
			return;
		}

		await this.#setCartData(newCartData);
	}

	async applyCoupon(couponCode: string) {
		if (couponCode === this.#cartData.couponCode) return;

		await fetch('/api/couponCode', {
			method: 'POST',
			body: JSON.stringify({ couponCode, force: true })
		});
		this.#cartData.couponCode = couponCode;

		if (this.#cartData.product) {
			this.#cartData.product.unsubscribe();

			if (this.#cartData.product.price.lookup_key) {
				this.#setCartData({
					...this.#cartData,
					product: new ReactiveProduct(
						this.#cartData.product.price.lookup_key,
						await getPrice(this.#cartData.product.price.lookup_key, true)
					)
				});
			}
		}
		invalidateAllPrices();
	}

	async selectByLookupKey(priceLookupKey: string) {
		const product = await getPrice(priceLookupKey, true);
		if (!product) {
			log.scope('Checkout').error('Cannot find product by lookup key', { priceLookupKey });
			return;
		}
		await this.select({ priceLookupKey, data: product });
	}

	async select(product: StripeProductData, config: Omit<CheckoutSessionData, 'product'> = {}) {
		this.#updatingBundle = config.productBundle
			? config.productBundle !== this.#cartData.productBundle
			: false;

		try {
			if (config.productBundle) {
				await shoppingCart.loadBundle(config.productBundle);
			}

			if (
				config.productBundle === this.#cartData.productBundle &&
				product.data.price.lookup_key === this.#cartData.product?.price.lookup_key &&
				product.data.promotionCode?.code === this.#cartData.product?.promotionCode?.code
			) {
				this.#updatingBundle = false;
				return;
			}

			if (!product.priceLookupKey) {
				this.#updatingBundle = false;
				log.scope('Checkout').error('Missing product lookup key', { product });
				return;
			}

			this.#cartData.previousLookupKey = this.#cartData.product?.price.lookup_key;

			const isDifferentProduct =
				product.data.price.lookup_key !== this.#cartData.product?.price.lookup_key ||
				product.data.promotionCode?.coupon.id !== this.#cartData.product?.promotionCode?.coupon.id;

			if (browser && isDifferentProduct) {
				this.#cartData.product?.unsubscribe();
			}

			const newProduct = isDifferentProduct
				? new ReactiveProduct(product.priceLookupKey, product.data)
				: this.#cartData.product;

			if (isDifferentProduct || this.#cartData.product?.promotionCode) {
				invalidatePrice(product.priceLookupKey);
			}

			if (dev) {
				log.scope('Checkout').debug('Selecting product', {
					lookupKey: product.priceLookupKey,
					product: newProduct
				});
			}

			await this.#setCartData({
				...this.#cartData,
				...config,
				product: newProduct
			});
		} catch (e) {
			log.scope('Checkout').error('Cannot select product', {
				error: (e as Error).message
			});
		} finally {
			this.#updatingBundle = false;
		}
	}

	async restore() {
		let previousLookupKey = this.#cartData.previousLookupKey ?? shoppingCart.bundleConfig.default_stripe_product?.priceLookupKey;

		if (!previousLookupKey) {
			log.scope('Checkout').error('Cannot restore product: missing lookup key');
			return;
		}

		if (this.#cartData.previousLookupKey === this.#cartData.product?.price.lookup_key) {
			previousLookupKey = shoppingCart.bundleConfig.default_stripe_product?.priceLookupKey;
			if (!previousLookupKey) {
				log.scope('Checkout').error('Cannot restore product: missing lookup key');
				return;
			}
		}

		if (this.#cartData.product) {
			this.#cartData.product.unsubscribe();
		}

		await this.#setCartData({
			...this.#cartData,
			previousLookupKey: undefined,
			product: new ReactiveProduct(
				previousLookupKey,
				await getPrice(previousLookupKey, true)
			)
		});
	}

	async submit() {
		this.#processing = true;
		this.#lastError = null;

		try {
			await event('Go to checkout clicked');

			if (!this.#stripeSession) {
				throw new Error('Stripe session error');
			}

			if (!this.#cartData || !this.#cartData.product) {
				throw new Error('Cannot initiate checkout: invalid cart data');
			}

			let session: Stripe.Response<Stripe.Checkout.Session> | Error | null = null;
			let attempts = 0;
			const timeoutValue = 10_000;
			const maxAttempts = 3;

			const timeout = new Promise<never>((_, reject) =>
				setTimeout(() => reject(new Error('session_timeout')), timeoutValue * (attempts + 1))
			);

			while (session === null && attempts < maxAttempts) {
				try {
					session = await Promise.race([this.#stripeSession, timeout]);
				} catch (e) {
					if (!(e instanceof Error) || e.message !== 'session_timeout') {
						log.scope('Checkout').error('Failed to create checkout session', { error: e });
					}

					if (attempts >= maxAttempts) {
						throw e;
					}
				}
				attempts++;
			}

			if (session === null) {
				throw new Error('Failed to create checkout session after multiple attempts');
			}

			if (session instanceof Error) {
				throw session;
			}

			await trackInitiateCheckout(session, this.#cartData.product.promotionCode);

			if (!session.url) {
				throw new Error('Stripe session error: missing checkout URL');
			}
			window.location.href = session.url;
		} catch (e) {
			this.#lastError = (e as Error).message;
			log.scope('Checkout').exception(e, { ...this.#cartData });
		} finally {
			this.#processing = false;
		}
	}

	async addUserLabel(label: string) {
		if (this.#cartData.userLabels?.has(label)) return;

		const userLabels = new Set(this.#cartData.userLabels || []);
		userLabels.add(label);

		await this.#setCartData({ ...this.#cartData, userLabels });

		event('Added User Label', { label });
	}

	async removeUserLabel(label: string) {
		if (!this.#cartData.userLabels?.has(label)) return;

		const userLabels = new Set(this.#cartData.userLabels || []);
		userLabels.delete(label);

		await this.#setCartData({ ...this.#cartData, userLabels });

		event('Removed User Label', { label });
	}

	hasUserLabel(label: string) {
		return this.#cartData.userLabels?.has(label);
	}
}

export const checkoutSession = new CheckoutSession();
